import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiPopover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { type ReactElement, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import type { MenuItemLook } from '@/theme/looks';
import { Button, IconButton } from './buttons';
import { ICON_SIZE } from './iconSize';
import { type AnchorPoint, anchorProps, useOpenState, useTrigger } from './overlayState';
import { Surface } from './surfaces';

const SIZE = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' } as const;

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title: string;
  size?: keyof typeof SIZE;
  footer?: ReactNode;
  closeLabel?: string;
  children: ReactNode;
}

/** Shows an MUI dialog. It keeps the focus and stops the body scroll. With `onClose`, it
 *  shows a close button and closes on Esc or on the backdrop. */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  footer,
  closeLabel,
  children,
}: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { className: cn('m-4 max-h-modal', SIZE[size]) } }}
    >
      <DialogTitle id={titleId} className="flex items-start justify-between gap-4 p-4">
        {title}
        {onClose && (
          <IconButton aria-label={closeLabel ?? t('common.close')} size="sm" onClick={onClose}>
            <X size={ICON_SIZE.lg} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers className="p-4">
        {children}
      </DialogContent>

      {footer && <DialogActions disableSpacing className="flex-wrap gap-2 p-4">{footer}</DialogActions>}
    </Dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Asks the user to confirm an action, in place of window.confirm(). On a destructive
 *  prompt, Cancel has the focus. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const cancel = cancelLabel ?? t('common.cancel');

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      closeLabel={cancel}
      footer={
        <>
          <Button variant="ghost" autoFocus={destructive} onClick={onCancel} disabled={loading}>
            {cancel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      <Typography variant="body1">{message}</Typography>
    </Modal>
  );
}

export interface UnsavedChangesDialogProps {
  open: boolean;
  onLeave: () => void;
  onStay: () => void;
}

/** Asks the user to leave a form that has unsaved changes, or to stay on it. */
export function UnsavedChangesDialog({ open, onLeave, onStay }: UnsavedChangesDialogProps) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={open}
      title={t('common.unsavedTitle')}
      message={t('common.unsavedBody')}
      confirmLabel={t('common.unsavedLeave')}
      cancelLabel={t('common.unsavedStay')}
      destructive
      onConfirm={onLeave}
      onCancel={onStay}
    />
  );
}

export interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  ariaLabel: string;
  align?: 'start' | 'end';
  /** Opens at a viewport coordinate instead of under the trigger. */
  anchorPoint?: AnchorPoint | null;
  /** Give this with onOpenChange to control the popover. If absent, the popover
   *  keeps its own state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The width it takes when the viewport allows it. Narrower screens cut it down. */
  width?: number;
  className?: string;
}

/** Shows an MUI popover with any content, where Menu shows a list of actions.
 *  It closes on Escape and on a click outside itself. */
export function Popover({
  trigger,
  children,
  ariaLabel,
  align = 'end',
  anchorPoint = null,
  open: controlledOpen,
  onOpenChange,
  width,
  className,
}: PopoverProps) {
  const [open, setOpen] = useOpenState(controlledOpen, onOpenChange);
  const { ref, anchorEl, element } = useTrigger(trigger, open, 'dialog', () => setOpen(!open));

  return (
    <>
      <div ref={ref} className="contents">
        {element}
      </div>

      <MuiPopover
        open={open}
        onClose={() => setOpen(false)}
        {...anchorProps(anchorEl, anchorPoint, align)}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': ariaLabel,
            style: { width, maxWidth: 'calc(100vw - 32px)' },
            className: cn('mt-1 p-3', className),
          },
        }}
      >
        {children}
      </MuiPopover>
    </>
  );
}

const ItemRoot = styled(MuiMenuItem, { name: 'KitMenuItem', slot: 'root' })<{ ownerState: MenuItemLook }>({});

export interface MenuItem {
  key: string;
  label: string;
  /** A second line under the label, where the label alone is not enough. */
  description?: string;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuProps {
  trigger: ReactNode;
  items: MenuItem[];
  align?: 'start' | 'end';
  ariaLabel: string;
  header?: ReactNode;
  /** Viewport coordinate to open at; absent opens under the trigger. */
  anchorPoint?: AnchorPoint | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Content above the items, with a divider under it. The keyboard focus skips it. */
function MenuHeader({ children }: { children: ReactNode }) {
  return (
    <li role="presentation">
      <div className="flex flex-col gap-0.5 px-2.5 py-2">{children}</div>
      <Divider className="mb-1" />
    </li>
  );
}
MenuHeader.muiSkipListHighlight = true;

/** Shows an MUI menu of actions under its trigger, or at a viewport point. */
export function Menu({
  trigger,
  items,
  align = 'end',
  ariaLabel,
  header,
  anchorPoint = null,
  open: controlledOpen,
  onOpenChange,
}: MenuProps) {
  const [open, setOpen] = useOpenState(controlledOpen, onOpenChange);
  const { ref, anchorEl, element } = useTrigger(trigger, open, 'menu', () => setOpen(!open));

  return (
    <>
      <div ref={ref} className="contents">
        {element}
      </div>

      <MuiMenu
        open={open}
        onClose={() => setOpen(false)}
        {...anchorProps(anchorEl, anchorPoint, align)}
        slotProps={{ list: { 'aria-label': ariaLabel }, paper: { className: 'min-w-44' } }}
      >
        {header && <MenuHeader>{header}</MenuHeader>}
        {items.map((item) => (
          <ItemRoot
            key={item.key}
            disabled={item.disabled}
            ownerState={{ destructive: Boolean(item.destructive) }}
            onClick={() => {
              item.onSelect();
              setOpen(false);
            }}
            className="gap-2"
          >
            {item.icon}
            {item.description ? (
              <ListItemText primary={item.label} secondary={item.description} />
            ) : (
              item.label
            )}
          </ItemRoot>
        ))}
      </MuiMenu>
    </>
  );
}

export interface TooltipProps {
  content: string;
  side?: 'top' | 'bottom';
  children: ReactElement;
}

/** Shows supplementary text on hover and on focus. The text describes the child element and
 *  does not replace its name. */
export function Tooltip({ content, side = 'top', children }: TooltipProps) {
  return (
    <MuiTooltip title={content} placement={side} describeChild>
      {children}
    </MuiTooltip>
  );
}

export interface ComingSoonProps {
  label: string;
  children: ReactNode;
}

/** Puts a disabled control in a span that can take focus and that has its name and tooltip. */
export function ComingSoon({ label, children }: ComingSoonProps) {
  return (
    <Tooltip content={label}>
      <Surface
        as="span"
        tone="none"
        radius="sm"
        role="button"
        tabIndex={0}
        aria-disabled
        aria-label={label}
        className="inline-flex cursor-not-allowed"
      >
        {children}
      </Surface>
    </Tooltip>
  );
}
